import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useBirthInfo } from '../../contexts/BirthInfoContext';
import { calculateFourPillars, calculateElementBalance, analyzeElements, calculateTenGods } from '../../utils/fourPillars';
import { usePdfDownload } from '../../hooks/usePdfDownload';
import SEOSchema from '../../components/feature/SEOSchema';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const lifetimeSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${siteUrl}/lifetime-report`,
  name: '평생 운세 리포트 — 사주팔자 대운 분석',
  description: '사주팔자와 10년 대운 흐름을 분석한 평생 운명 지도. 인생 4단계(청년기·장년기·중년기·노년기)와 커리어·재물·건강의 정점 시기를 확인하세요.',
  url: `${siteUrl}/lifetime-report`,
  provider: {
    '@type': 'Organization',
    name: 'DESTINY GPT',
    url: siteUrl,
  },
  serviceType: '평생 운세 리포트',
  areaServed: { '@type': 'Country', name: '대한민국' },
  inLanguage: 'ko-KR',
};

const TEN_STEMS = ['갑', '을', '병', '정', '무', '기', '경', '신', '임', '계'];
const TWELVE_BRANCHES = ['자', '축', '인', '묘', '진', '사', '오', '미', '신', '유', '술', '해'];

function calcDecade(birthYear: number, dayStemIdx: number, startAge: number): { stem: string; branch: string; score: number }[] {
  const decades: { stem: string; branch: string; score: number }[] = [];
  for (let i = 0; i < 9; i++) {
    const stemIdx = (dayStemIdx + i * 2) % 10;
    const branchIdx = (i * 2) % 12;
    const seed = (birthYear + stemIdx * 7 + branchIdx * 13) % 100;
    const score = (seed % 4) + 2;
    decades.push({ stem: TEN_STEMS[stemIdx], branch: TWELVE_BRANCHES[branchIdx], score });
  }
  return decades;
}

const LIFE_PHASES = [
  { id: 'youth', label: '청년기', range: '0 — 30세', icon: 'ri-seedling-line', desc: '기초를 다지고 자신을 발견하는 시기', color: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-500/10 border-emerald-500/25' },
  { id: 'prime', label: '장년기', range: '30 — 50세', icon: 'ri-fire-line', desc: '가장 강력한 성취와 확장의 시기', color: 'from-amber-500 to-orange-600', bg: 'bg-amber-500/10 border-amber-500/25' },
  { id: 'mature', label: '중년기', range: '50 — 65세', icon: 'ri-plant-line', desc: '결실과 사회적 영향력이 완성되는 시기', color: 'from-violet-500 to-purple-600', bg: 'bg-violet-500/10 border-violet-500/25' },
  { id: 'senior', label: '노년기', range: '65세 이상', icon: 'ri-moon-fill', desc: '지혜와 여유로 인생을 완성하는 시기', color: 'from-sky-500 to-indigo-600', bg: 'bg-sky-500/10 border-sky-500/25' },
];

const LIFE_PILLARS = [
  { key: 'career', icon: 'ri-briefcase-4-line', label: '커리어 정점', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/25' },
  { key: 'wealth', icon: 'ri-money-dollar-circle-line', label: '재물 최성기', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/25' },
  { key: 'love', icon: 'ri-heart-3-line', label: '인연 · 결혼', color: 'text-rose-400', bg: 'bg-rose-500/10 border-rose-500/25' },
  { key: 'health', icon: 'ri-heart-pulse-line', label: '건강 집중기', color: 'text-sky-400', bg: 'bg-sky-500/10 border-sky-500/25' },
  { key: 'wisdom', icon: 'ri-star-line', label: '영적 성장', color: 'text-violet-300', bg: 'bg-violet-500/10 border-violet-500/25' },
];

const DAY_STEM_LIFE_MSG: Record<string, string> = {
  갑: '갑목(甲木)의 삶은 곧게 자라는 대나무처럼 한 방향으로 꿋뚝이 전진합니다. 선구자로서 길을 만들고, 후반생에 반드시 빛을 발합니다.',
  을: '을목(乙木)의 삶은 넝쿨처럼 유연하게 환경에 적응하며 끝내 꽃을 피웁니다. 인간관계에서 가장 큰 복이 찾아옵니다.',
  병: '병화(丙火)의 삶은 태양처럼 주변을 비추며 살아갑니다. 대중과의 인연이 강하고, 나눌수록 더 많이 채워지는 삶의 패턴을 가집니다.',
  정: '정화(丁火)의 삶은 촛불처럼 어둠을 밝히는 전문가의 여정입니다. 하나를 깊이 파면 반드시 세상이 알아주는 명인이 됩니다.',
  무: '무토(戊土)의 삶은 산처럼 묵직하게 모든 것을 품어안습니다. 신뢰가 최대 자산이며, 중년 이후에 진가가 발휘되는 만개형 사주입니다.',
  기: '기토(己土)의 삶은 비옥한 토양처럼 누구에게나 품요로운 터전을 제공합니다. 현실적 감각과 성실함이 인생을 품요롭게 만듭니다.',
  경: '경금(庚金)의 삶은 날카로운 검처럼 결단력이 인생을 개척합니다. 단련될수록 빛나는 사주로, 역경이 오히려 도약의 발판이 됩니다.',
  신: '신금(辛金)의 삶은 보석처럼 시간이 지날수록 빛납니다. 섬세한 감수성과 완벽주의가 독창적인 삶의 예술을 만들어냅니다.',
  임: '임수(壬水)의 삶은 강처럼 큰 방향을 가지고 흐릅니다. 전략과 지혜로 장애를 돌아가며 결국 바다에 닿는 대운명의 흐름입니다.',
  계: '계수(癸水)의 삶은 이슬처럼 섬세하게 스며들며 큰 영향을 미칩니다. 감성과 직관이 남다른 삶의 방식으로 사람들의 가축에 남습니다.',
};

function getLifePillarAges(birthDate: string, pillar: string): string {
  const hash = birthDate.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const bases: Record<string, number> = { career: 35, wealth: 42, love: 28, health: 55, wisdom: 60 };
  const base = bases[pillar] ?? 40;
  const offset = (hash + pillar.charCodeAt(0)) % 8;
  return `${base + offset - 3}세 ~ ${base + offset + 5}세`;
}

const SCORE_COLOR: Record<number, string> = {
  5: 'text-amber-400 bg-amber-500/15 border-amber-500/30',
  4: 'text-emerald-400 bg-emerald-500/15 border-emerald-500/30',
  3: 'text-purple-300 bg-purple-500/15 border-purple-500/30',
  2: 'text-orange-400 bg-orange-500/15 border-orange-500/30',
  1: 'text-red-400 bg-red-500/15 border-red-500/30',
};

const SCORE_LABEL: Record<number, string> = { 5: '대길(大吉)', 4: '길(吉)', 3: '평(平)', 2: '주의', 1: '흉(凶)' };

export default function LifetimeReportPage() {
  const navigate = useNavigate();
  const { birthInfo } = useBirthInfo();
  const { downloadLifetimePdf } = usePdfDownload();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [activePhase, setActivePhase] = useState('prime');
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (birthInfo) {
      setIsGenerating(true);
      const t = setTimeout(() => { setIsGenerating(false); setIsReady(true); }, 2500);
      return () => clearTimeout(t);
    }
  }, [birthInfo]);

  if (!birthInfo) {
    return (
      <div className="min-h-screen bg-cosmic-gradient">
        <SEOSchema schema={lifetimeSchema} id="lifetime-schema" />
        <Navbar />
        <main className="pt-20 min-h-[80vh] flex items-center justify-center px-4">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary-gold/20 to-amber-600/10 border border-primary-gold/30 flex items-center justify-center">
              <i className="ri-infinity-line text-primary-gold text-4xl"></i>
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">생년월일 정보가 필요합니다</h2>
            <p className="text-purple-300 mb-6 text-sm">평생 운세 리포트를 생성하려면 먼저 생년월일을 입력해주세요.</p>
            <button onClick={() => navigate('/')} className="px-6 py-3 bg-gradient-to-r from-primary-gold to-amber-500 rounded-full text-black font-bold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer">
              정보 입력하러 가기
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const fp = calculateFourPillars(birthInfo.birthDate, birthInfo.birthTime);
  const balance = calculateElementBalance(fp);
  const elAnalysis = analyzeElements(balance);
  const tenGods = calculateTenGods(fp);
  const birthYear = new Date(birthInfo.birthDate).getFullYear();
  const currentYear = new Date().getFullYear();
  const age = currentYear - birthYear;
  const dayStemIdx = TEN_STEMS.indexOf(fp.day.stem);
  const decades = calcDecade(birthYear, dayStemIdx, 0);
  const lifeMsg = DAY_STEM_LIFE_MSG[fp.day.stem] ?? '';
  const currentDecadeIdx = Math.floor(age / 10);

  // Life strengths/weaknesses based on tenGods
  const strengths = tenGods.dominantGods.slice(0, 2).map((g) => g.god + ' — ' + g.shortMeaning);
  const cautions = [
    '과욕(過慾) — 기운이 강할 때 겸손함을 잃지 마세요',
    '고집(固執) — 유연성을 유지해야 귀인이 머뭅니다',
  ];

  const handleDownloadPdf = () => {
    setIsDownloading(true);
    const lifePillars = LIFE_PILLARS.map((pillar) => {
      const peakAges = getLifePillarAges(birthInfo.birthDate, pillar.key);
      const hash = birthInfo.birthDate.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
      const score = ((hash + pillar.key.charCodeAt(0)) % 3) + 3;
      return { label: pillar.label, peakAges, score };
    });
    downloadLifetimePdf({
      birthInfo,
      dayStem: fp.day.stem,
      dayBranch: fp.day.branch,
      strongestElement: elAnalysis.strongest,
      weakestElement: elAnalysis.weakest,
      lifeMessage: lifeMsg,
      decades,
      lifePillars,
      strengths,
      cautions,
      dominantGods: tenGods.dominantGods,
    });
    setTimeout(() => setIsDownloading(false), 2000);
  };

  return (
    <div className="min-h-screen bg-cosmic-gradient">
      <SEOSchema schema={lifetimeSchema} id="lifetime-schema" />
      <Navbar />
      <main className="pt-24 sm:pt-28">
        {/* Hero */}
        <section className="relative pb-8 px-4 sm:px-6">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <img
              src="https://public.readdy.ai/ai/img_res/29d2abed1cf7755fe6b48f3292f22263.jpg"
              alt="lifetime report hero"
              className="w-full h-full object-cover object-top opacity-25"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-primary-dark/60 via-primary-dark/70 to-primary-dark"></div>
          </div>

          <div className="relative z-10 max-w-4xl mx-auto text-center pt-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-gold/15 border border-primary-gold/40 mb-5">
              <i className="ri-infinity-line text-primary-gold text-sm"></i>
              <span className="text-primary-gold text-xs font-bold tracking-widest uppercase">평생 운세 리포트</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-white mb-4 leading-tight">
              {birthInfo.name ? birthInfo.name + '님의' : '당신의'}<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-gold via-amber-300 to-primary-gold">
                평생 운명 지도
              </span>
            </h1>
            <p className="text-purple-300/80 text-base mb-6">
              {fp.day.stem}{fp.day.branch} 일주 · {elAnalysis.strongest} 기운 · 현재 {age}세
            </p>

            {/* Summary chips */}
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { icon: 'ri-yin-yang-line', label: '일주', value: fp.day.stem + fp.day.branch },
                { icon: 'ri-bar-chart-2-line', label: '주 오행', value: elAnalysis.strongest },
                { icon: 'ri-node-tree', label: '주 십신', value: tenGods.dominantGods[0]?.god ?? '-' },
                { icon: 'ri-calendar-todo-line', label: '현재 대운', value: decades[currentDecadeIdx]?.stem + decades[currentDecadeIdx]?.branch + ' 대운' },
              ].map((c) => (
                <div key={c.label} className="flex items-center gap-2 px-4 py-2 rounded-full glass-card border border-white/10">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${c.icon} text-primary-gold text-xs`}></i>
                  </div>
                  <span className="text-purple-300/60 text-xs">{c.label}</span>
                  <span className="text-white text-xs font-bold">{c.value}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {isGenerating && (
          <div className="max-w-3xl mx-auto px-4 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-6 relative">
              <div className="absolute inset-0 rounded-full border-4 border-primary-gold/30"></div>
              <div className="absolute inset-0 rounded-full border-4 border-primary-gold border-t-transparent animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <i className="ri-infinity-line text-primary-gold text-3xl"></i>
              </div>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">평생 운세를 분석 중입니다...</h3>
            <p className="text-purple-300/60 text-sm">사주팔자, 대운, 오행 흐름을 종합 계산하고 있어요</p>
          </div>
        )}

        {isReady && (
          <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-20 space-y-10">

            {/* PDF Download Button */}
            <div className="flex justify-end">
              <button
                onClick={handleDownloadPdf}
                disabled={isDownloading}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/5 border border-white/15 text-purple-200 hover:border-primary-gold/40 hover:text-primary-gold transition-all text-sm font-semibold cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  {isDownloading
                    ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                    : <i className="ri-download-2-line text-sm"></i>
                  }
                </div>
                <span>{isDownloading ? 'PDF 준비 중...' : 'PDF 저장'}</span>
              </button>
            </div>

            {/* Life message */}
            <div className="p-6 rounded-2xl bg-gradient-to-br from-amber-900/25 to-amber-800/10 border border-amber-500/30">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-amber-500/20 border border-amber-500/30 flex-shrink-0">
                  <i className="ri-quill-pen-line text-amber-400 text-xl"></i>
                </div>
                <div>
                  <p className="text-amber-400 text-xs font-bold uppercase tracking-wide mb-2">{fp.day.stem}{fp.day.branch} 일주 — 삶의 메시지</p>
                  <p className="text-white/90 text-sm sm:text-base leading-relaxed">{lifeMsg}</p>
                </div>
              </div>
            </div>

            {/* Life phases */}
            <div className="p-6 rounded-2xl bg-gradient-to-br from-primary-dark/80 to-mystic-dark/80 border border-primary-gold/20">
              <h2 className="text-white font-bold text-lg mb-6 flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-user-star-line text-primary-gold text-base"></i>
                </div>
                인생 4단계 운세
              </h2>
              <div className="flex gap-2 flex-wrap mb-6">
                {LIFE_PHASES.map((phase) => (
                  <button
                    key={phase.id}
                    onClick={() => setActivePhase(phase.id)}
                    className={`px-4 py-2 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap border ${
                      activePhase === phase.id ? 'bg-primary-gold text-black border-primary-gold' : 'bg-white/5 border-white/10 text-purple-300 hover:border-primary-gold/30'
                    }`}
                  >
                    {phase.label} ({phase.range})
                  </button>
                ))}
              </div>

              {LIFE_PHASES.filter((p) => p.id === activePhase).map((phase) => {
                const phaseScore = (['youth', 'prime', 'mature', 'senior'].indexOf(phase.id) + 3) as number;
                const seedOffset = birthYear % 4;
                const adjustedScore = Math.min(5, Math.max(2, phaseScore - seedOffset % 2));
                return (
                  <div key={phase.id} className={`p-5 rounded-2xl ${phase.bg} border`}>
                    <div className="flex items-start gap-4">
                      <div className={`w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br ${phase.color} flex-shrink-0`}>
                        <i className={`${phase.icon} text-white text-2xl`}></i>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-white font-bold text-base">{phase.label}</h3>
                          <span className="text-purple-300/60 text-sm">{phase.range}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${SCORE_COLOR[adjustedScore]}`}>{SCORE_LABEL[adjustedScore]}</span>
                        </div>
                        <p className="text-purple-200/80 text-sm mb-3">{phase.desc}</p>

                        {phase.id === 'youth' && (
                          <div className="space-y-1.5 text-xs text-purple-300/70">
                            <p>• {fp.day.stem}일간의 청년기: 자신의 오행 기운을 발견하고 방향을 정하는 중요한 시기</p>
                            <p>• {elAnalysis.strongest} 기운이 강한 당신은 이 에너지를 학업과 커리어 기초에 집중하세요</p>
                            <p>• 10대 후반~20대: {decades[0]?.stem}{decades[0]?.branch} 대운의 영향으로 {decades[0]?.score >= 4 ? '순탄한 시작이 기대됩니다' : '도전을 극복하며 강해지는 시기입니다'}</p>
                          </div>
                        )}
                        {phase.id === 'prime' && (
                          <div className="space-y-1.5 text-xs text-purple-300/70">
                            <p>• 30~50대는 사주에서 재성(財星)과 관성(官星)이 가장 활발해지는 시기</p>
                            <p>• 당신의 십신 구성 ({tenGods.dominantGods.slice(0, 2).map((g) => g.god).join(', ')}) 기준으로 직업과 재물에서 특히 강한 기운이 발동합니다</p>
                            <p>• 이 시기 귀인을 잘 활용하고 인간관계에 투자하세요</p>
                          </div>
                        )}
                        {phase.id === 'mature' && (
                          <div className="space-y-1.5 text-xs text-purple-300/70">
                            <p>• 50~65세는 인성(印星)과 식상(食傷)이 빛을 발하는 시기 — 경험과 지혜가 최대 자산</p>
                            <p>• {elAnalysis.weakest} 기운을 보완하는 활동(건강·취미·인간관계)에 집중하세요</p>
                            <p>• 사회적 영향력과 인망이 절정에 달하는 시기입니다</p>
                          </div>
                        )}
                        {phase.id === 'senior' && (
                          <div className="space-y-1.5 text-xs text-purple-300/70">
                            <p>• 65세 이후 비겁(比劫) 기운이 활성화되어 자유롭고 주체적인 삶이 펼쳐집니다</p>
                            <p>• 지혜를 나누고 후배를 이끄는 역할에서 진정한 행복을 찾게 됩니다</p>
                            <p>• {fp.day.stem}일간의 노년기는 건강에 집중하고 영적 성장을 추구하는 아름다운 시기입니다</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Decade Fortune Timeline */}
            <div className="p-6 rounded-2xl bg-gradient-to-br from-primary-dark/80 to-mystic-dark/80 border border-primary-gold/20">
              <h2 className="text-white font-bold text-lg mb-2 flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-time-line text-primary-gold text-base"></i>
                </div>
                10년 대운(大운) 흐름
              </h2>
              <p className="text-purple-300/60 text-sm mb-6">각 10년 주기의 대운 기운을 확인하세요</p>

              <div className="overflow-x-auto pb-2">
                <div className="flex gap-3 min-w-max">
                  {decades.map((d, idx) => {
                    const startAge = idx * 10;
                    const endAge = startAge + 9;
                    const startYear = birthYear + startAge;
                    const isCurrent = idx === currentDecadeIdx;
                    const cfg = SCORE_COLOR[d.score] ?? SCORE_COLOR[3];
                    const bar = d.score >= 4 ? 'bg-amber-400' : d.score >= 3 ? 'bg-purple-400' : 'bg-orange-400';
                    return (
                      <div key={idx} className={`relative flex flex-col items-center p-4 rounded-2xl border transition-all w-28 ${isCurrent ? 'border-primary-gold bg-primary-gold/15 scale-105' : cfg}`}>
                        {isCurrent && (
                          <div className="absolute -top-2 left-1/2 -translate-x-1/2">
                            <span className="text-[9px] bg-primary-gold text-black font-black px-2 py-0.5 rounded-full whitespace-nowrap">현재</span>
                          </div>
                        )}
                        <p className="text-white font-black text-lg">{d.stem}{d.branch}</p>
                        <p className="text-purple-300/60 text-[10px]">{startYear}~{startYear + 9}년</p>
                        <p className="text-purple-300/60 text-[10px] mb-2">{startAge}~{endAge}세</p>
                        <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden mb-1.5">
                          <div className={`h-full rounded-full ${bar}`} style={{ width: `${(d.score / 5) * 100}%` }}></div>
                        </div>
                        <p className={`text-xs font-bold ${cfg.split(' ')[0]}`}>{SCORE_LABEL[d.score]}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* 5 Life Pillars */}
            <div className="p-6 rounded-2xl bg-gradient-to-br from-primary-dark/80 to-mystic-dark/80 border border-primary-gold/20">
              <h2 className="text-white font-bold text-lg mb-6 flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-layout-grid-line text-primary-gold text-base"></i>
                </div>
                인생 5대 영역 정점 시기
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {LIFE_PILLARS.map((pillar) => {
                  const peakAges = getLifePillarAges(birthInfo.birthDate, pillar.key);
                  const hash = birthInfo.birthDate.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
                  const score = ((hash + pillar.key.charCodeAt(0)) % 3) + 3;
                  return (
                    <div key={pillar.key} className={`p-5 rounded-2xl border ${pillar.bg}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/5">
                          <i className={`${pillar.icon} ${pillar.color} text-lg`}></i>
                        </div>
                        <div>
                          <p className="text-white font-bold text-sm">{pillar.label}</p>
                          <p className={`text-xs font-semibold ${pillar.color}`}>정점: {peakAges}</p>
                        </div>
                      </div>
                      <div className="flex gap-0.5 mb-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <div key={i} className={`flex-1 h-1.5 rounded-full ${i < score ? pillar.color.replace('text-', 'bg-') : 'bg-white/10'}`}></div>
                        ))}
                      </div>
                      <p className="text-purple-300/60 text-xs">{SCORE_LABEL[score]}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Strengths & Cautions */}
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="p-5 rounded-2xl bg-emerald-500/10 border border-emerald-500/25">
                <h3 className="text-emerald-400 font-bold text-sm mb-4 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-shield-star-line text-sm"></i>
                  </div>
                  평생 핵심 강점
                </h3>
                <div className="space-y-2">
                  {strengths.map((s, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-checkbox-circle-fill text-emerald-400 text-xs"></i>
                      </div>
                      <p className="text-emerald-200/80 text-xs leading-relaxed">{s}</p>
                    </div>
                  ))}
                  <div className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-emerald-400 text-xs"></i>
                    </div>
                    <p className="text-emerald-200/80 text-xs leading-relaxed">{elAnalysis.description}</p>
                  </div>
                </div>
              </div>

              <div className="p-5 rounded-2xl bg-orange-500/10 border border-orange-500/25">
                <h3 className="text-orange-400 font-bold text-sm mb-4 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-alert-line text-sm"></i>
                  </div>
                  평생 주의 사항
                </h3>
                <div className="space-y-2">
                  {cautions.map((c, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-error-warning-fill text-orange-400 text-xs"></i>
                      </div>
                      <p className="text-orange-200/80 text-xs leading-relaxed">{c}</p>
                    </div>
                  ))}
                  <div className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-error-warning-fill text-orange-400 text-xs"></i>
                    </div>
                    <p className="text-orange-200/80 text-xs leading-relaxed">부족한 오행 {elAnalysis.weakest} 보충 — 관련 색상·방향·음식으로 균형을 맞추세요</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Link to annual report */}
            <div className="p-6 rounded-2xl bg-gradient-to-br from-indigo-900/25 to-purple-900/15 border border-indigo-500/25 text-center">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-2xl bg-indigo-500/10 border border-indigo-500/20">
                <i className="ri-calendar-2-line text-indigo-300 text-2xl"></i>
              </div>
              <h3 className="text-white font-bold text-base mb-1">올해 구체적 운세가 궁금하신가요?</h3>
              <p className="text-purple-300/70 text-sm mb-4">1년 운세 리포트에서 월별 상세 운세를 확인하세요</p>
              <Link to="/annual-report" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-bold text-sm hover:shadow-lg hover:shadow-indigo-500/30 transition-all whitespace-nowrap">
                <i className="ri-calendar-2-line text-sm"></i>
                <span>1년 운세 리포트 보기</span>
              </Link>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
